import { Stack, Typography } from '@mui/joy';
import { useTheme } from '@mui/joy/styles';
import { CaretDown, Check, Link as LinkIcon } from '@phosphor-icons/react';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'core/components';
import { LabeledValue, Link, TextEllipsis, UpdatedAtSubtitle } from 'core/elements';
import { getFullUrl } from 'core/utils';
import { oldManufacturerDetailsPath } from 'manufacturers/routes/routePaths';
import { partListPath } from 'parts/routes';
import { IPartDetails, IPartManufacturer } from 'parts/types';
import { Color, Direction, Level, Variant } from 'theme/enums';

import { DatasheetPreviewButton } from './DatasheetPreview';
import { EditPartSetupButton } from './EditPartSetup';

interface IProps {
  part?: IPartDetails | null;
  manufacturer?: IPartManufacturer | null;
  updatedAt?: string | null;
  title?: ReactNode;
  chip?: ReactNode;
  isCollapsed?: boolean;
  onExpand?: () => void;
}

export const Subtitle = ({
  part,
  manufacturer,
  updatedAt = null,
  title,
  chip,
  isCollapsed = false,
  onExpand,
}: IProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { t } = useTranslation();
  const theme = useTheme();

  useEffect(
    () => () => {
      if (copyTimerRef.current) {
        clearTimeout(copyTimerRef.current);
      }
    },
    []
  );

  const onCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getFullUrl(partListPath(part?.number)));
      setIsCopied(true);

      if (copyTimerRef.current) {
        clearTimeout(copyTimerRef.current);
      }

      copyTimerRef.current = setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy link', error);
    }
  };

  const actionButtons =
    isCollapsed && onExpand ? (
      <Button
        endDecorator={<CaretDown />}
        variant={Variant.SOFT}
        color={Color.NEUTRAL}
        text={t('buttonLabelShowPartDetails')}
        onClick={onExpand}
      />
    ) : (
      <Stack direction={Direction.ROW} sx={{ gap: 1 }}>
        <EditPartSetupButton part={part} manufacturer={manufacturer} />
        <DatasheetPreviewButton part={part} />
        {part?.number && (
          <Button
            startDecorator={isCopied ? <Check /> : <LinkIcon />}
            color={isCopied ? Color.SUCCESS : Color.PRIMARY}
            variant={Variant.SOFT}
            text={isCopied ? t('buttonLabelCopied') : t('buttonLabelCopyLink')}
            onClick={onCopyLink}
          />
        )}
      </Stack>
    );

  return (
    <>
      {title && (
        <Stack
          direction={Direction.ROW}
          sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 1 }}
        >
          <Stack direction={Direction.ROW} sx={{ alignItems: 'center', gap: 1 }}>
            <Typography level={Level.H3}>{title}</Typography>
            {chip}
          </Stack>
          {actionButtons}
        </Stack>
      )}

      <Stack
        direction={Direction.ROW}
        sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 1 }}
      >
        <Typography color={Color.NEUTRAL} level={Level.TITLE_SM}>
          {updatedAt && <UpdatedAtSubtitle updatedAt={updatedAt} />}

          {manufacturer?.id != null && manufacturer.name && (
            <Link
              href={oldManufacturerDetailsPath(manufacturer.id)}
              target="_blank"
              rel="noopener noreferrer"
              withIcon
              sx={!updatedAt && { span: { marginLeft: 0 } }}
            >
              {manufacturer.name}
            </Link>
          )}
        </Typography>

        {!title && actionButtons}
      </Stack>

      <Stack>
        <LabeledValue
          label={t('labelDescription')}
          value={
            <TextEllipsis
              content={part?.descriptionLong}
              sx={{ color: theme.palette.neutral[800], fontWeight: 600 }}
              maxRows={3}
            />
          }
          options={{
            labelLevel: Level.BODY_SM,
            inline: true,
            valueSx: { color: theme.palette.neutral[800], fontWeight: 600 },
            wrapperProps: { sx: { alignItems: 'flex-start' } },
          }}
        />

        {part?.orderingNumber && (
          <LabeledValue
            label={t('labelOrderingNumber')}
            value={part.orderingNumber}
            options={{
              labelLevel: Level.BODY_SM,
              inline: true,
              valueSx: { color: theme.palette.neutral[800], fontWeight: 600 },
              wrapperProps: { sx: { alignItems: 'flex-start' } },
            }}
          />
        )}

        {part?.engineeringNumber && (
          <LabeledValue
            label={t('labelEngineeringNumber')}
            value={part.engineeringNumber}
            options={{
              labelLevel: Level.BODY_SM,
              inline: true,
              valueSx: { color: theme.palette.neutral[800], fontWeight: 600 },
              wrapperProps: { sx: { alignItems: 'flex-start' } },
            }}
          />
        )}
      </Stack>
    </>
  );
};
